import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import AuthGuard from '@/components/auth-guard';
import {
  type PeriodSelection,
  PeriodSelector,
} from '@/components/dashboard/period-selector';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { TagMultiSelect } from '@/components/ui/tag-multi-select';
import { trpc } from '@/utils/trpc';

export const Route = createFileRoute('/tags-dashboard')({
  component: TagsDashboard,
});

interface TagLimitDialogProps {
  tagSlug: string;
  currentLimit: number | null;
  onSaved: () => void;
}

function TagLimitDialog({
  tagSlug,
  currentLimit,
  onSaved,
}: TagLimitDialogProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const setLimitMutation = useMutation({
    ...trpc.tags.setLimit.mutationOptions(),
    onSuccess: () => {
      toast.success('Limite atualizado com sucesso!');
      onSaved();
      setOpen(false);
    },
    onError: () => {
      toast.error('Não foi possível salvar o limite. Tente novamente.');
    },
  });

  useEffect(() => {
    if (open) {
      setInputValue(currentLimit !== null ? currentLimit.toString() : '');
    }
  }, [open, currentLimit]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = inputValue.trim();

    let parsed: number | null = null;

    if (trimmed.length > 0) {
      const normalized = Number(trimmed.replace(',', '.'));
      if (Number.isNaN(normalized) || normalized <= 0) {
        toast.error('Informe um valor maior que zero.');
        return;
      }
      parsed = normalized;
    }

    setLimitMutation.mutate({ tag: tagSlug, spendingLimit: parsed });
  };

  const handleClearLimit = () => {
    setLimitMutation.mutate({ tag: tagSlug, spendingLimit: null });
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          {currentLimit !== null ? 'Editar limite' : 'Definir limite'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle>Limite para #{tagSlug}</DialogTitle>
          <DialogDescription>
            Configure um valor máximo de gastos para monitorar esta tag.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor={`tag-limit-${tagSlug}`}>Valor máximo</Label>
            <Input
              autoFocus
              id={`tag-limit-${tagSlug}`}
              inputMode="decimal"
              min="0"
              onChange={(event) => setInputValue(event.target.value)}
              placeholder="Ex: 500"
              value={inputValue}
            />
            <p className="text-muted-foreground text-xs">
              Informe um valor maior que zero ou deixe em branco para remover o
              limite atual.
            </p>
          </div>
          <DialogFooter>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              {currentLimit !== null && (
                <Button
                  className="w-full sm:w-auto"
                  disabled={setLimitMutation.isPending}
                  onClick={handleClearLimit}
                  type="button"
                  variant="outline"
                >
                  Remover limite
                </Button>
              )}
              <div className="flex w-full gap-2 sm:ml-auto sm:w-auto sm:justify-end">
                <Button
                  className="w-full sm:w-auto"
                  disabled={setLimitMutation.isPending}
                  onClick={() => setOpen(false)}
                  type="button"
                  variant="outline"
                >
                  Cancelar
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  disabled={setLimitMutation.isPending}
                  type="submit"
                >
                  {setLimitMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TagsDashboard() {
  const today = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodSelection>({
    type: 'current',
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: new Date(today.getFullYear(), today.getMonth() + 1, 0),
    label: 'Mês atual',
  });
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Load saved preferences
  const prefsQuery = useQuery({
    ...trpc.household.getPreferences.queryOptions({}),
  });
  useEffect(() => {
    const prefs: any = prefsQuery.data || {};
    if (prefs.tagsDashboard?.tags && Array.isArray(prefs.tagsDashboard.tags)) {
      setSelectedTags(prefs.tagsDashboard.tags as string[]);
    }
    if (prefs.tagsDashboard?.period) {
      const p = prefs.tagsDashboard.period as any;
      if (p?.startDate && p?.endDate && p?.type && p?.label) {
        setSelectedPeriod({
          type: p.type,
          startDate: new Date(p.startDate),
          endDate: new Date(p.endDate),
          label: p.label,
        });
      }
    }
  }, [prefsQuery.data]);

  const dashboardQuery = useQuery({
    ...trpc.tags.dashboard.queryOptions({
      startDate: selectedPeriod.startDate.toISOString(),
      endDate: selectedPeriod.endDate.toISOString(),
      tags: selectedTags,
    }),
    enabled: selectedTags.length > 0,
  });

  // Save preferences when tags or period change
  const savePrefsMutation = useMutation({
    ...trpc.household.savePreferences.mutationOptions(),
  });
  useEffect(() => {
    if (!prefsQuery.isLoading) {
      savePrefsMutation.mutate({
        preferences: {
          tagsDashboard: {
            tags: selectedTags,
            period: {
              type: selectedPeriod.type,
              startDate: selectedPeriod.startDate.toISOString(),
              endDate: selectedPeriod.endDate.toISOString(),
              label: selectedPeriod.label,
            },
          },
        },
      });
    }
  }, [
    selectedTags,
    selectedPeriod,
    prefsQuery.isLoading,
    savePrefsMutation.mutate,
  ]);
  const tagCards = dashboardQuery.data || [];
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
    []
  );

  const formatCurrency = (value: number) => currencyFormatter.format(value);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    []
  );

  const handleLimitSaved = () => {
    void dashboardQuery.refetch();
  };

  return (
    <AuthGuard requireAuth={true}>
      <div className="container mx-auto space-y-6 px-4 py-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-bold text-2xl">Painel por Tags</h1>
          <div className="flex-1">
            <PeriodSelector
              onPeriodChange={setSelectedPeriod}
              selectedPeriod={selectedPeriod}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="w-full max-w-xl">
            <TagMultiSelect onChange={setSelectedTags} value={selectedTags} />
          </div>
        </div>

        {selectedTags.length === 0 && (
          <p className="text-muted-foreground">
            Selecione uma ou mais tags para rastrear.
          </p>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tagCards.map((card) => {
            const limitValue = card.spendingLimit ?? 0;
            const hasLimit = limitValue > 0;
            const spentPercentage = hasLimit
              ? (card.totalSpent / limitValue) * 100
              : null;
            const clampedProgress =
              spentPercentage !== null
                ? Math.min(Math.max(spentPercentage, 0), 100)
                : 0;
            const overLimitPercentage =
              spentPercentage !== null && spentPercentage > 100
                ? spentPercentage - 100
                : null;
            const remainingAmount = hasLimit
              ? limitValue - card.totalSpent
              : null;

            return (
              <Card key={card.tag}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>#{card.tag}</span>
                    <span className="font-normal text-muted-foreground text-sm">
                      {card.transactionCount} transação
                      {card.transactionCount !== 1 ? 'ões' : ''}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="font-semibold text-2xl">
                      {formatCurrency(Math.abs(card.netAmount))}
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {card.netAmount >= 0
                        ? 'Saldo positivo'
                        : 'Saldo negativo'}
                    </p>
                  </div>

                  {card.totalSpent > 0 && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Gastos: </span>
                      <span className="font-medium text-red-600">
                        {formatCurrency(card.totalSpent)}
                      </span>
                    </div>
                  )}

                  {card.totalIncome > 0 && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Receitas: </span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(card.totalIncome)}
                      </span>
                    </div>
                  )}

                  <div className="space-y-3 rounded-lg border border-border/80 border-dashed p-3">
                    <div className="flex items-center justify-between font-medium text-sm">
                      <span>Limite de gastos</span>
                      <TagLimitDialog
                        currentLimit={card.spendingLimit}
                        onSaved={handleLimitSaved}
                        tagSlug={card.tag}
                      />
                    </div>
                    {hasLimit ? (
                      <>
                        <div className="flex items-center justify-between text-muted-foreground text-xs">
                          <span>Limite: {formatCurrency(limitValue)}</span>
                          <span>Gasto: {formatCurrency(card.totalSpent)}</span>
                        </div>
                        <Progress className="h-2" value={clampedProgress} />
                        <div className="flex items-center justify-between text-xs">
                          <span
                            className={
                              spentPercentage !== null && spentPercentage >= 100
                                ? 'font-medium text-red-600'
                                : 'text-muted-foreground'
                            }
                          >
                            {spentPercentage !== null
                              ? `${Math.round(spentPercentage)}% do limite`
                              : ''}
                          </span>
                          {overLimitPercentage !== null ? (
                            <span className="font-medium text-red-600">
                              +{Math.round(overLimitPercentage)}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              Restante:{' '}
                              {formatCurrency(
                                Math.max(remainingAmount ?? 0, 0)
                              )}
                            </span>
                          )}
                        </div>
                        {overLimitPercentage !== null && (
                          <p className="font-medium text-red-600 text-xs">
                            Você ultrapassou o limite em{' '}
                            {Math.round(overLimitPercentage)}%.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        Defina um limite para acompanhar melhor seus gastos
                        nesta tag.
                      </p>
                    )}
                  </div>

                  {card.lastTransactionDate && (
                    <div className="text-muted-foreground text-xs">
                      Última transação:{' '}
                      {dateFormatter.format(new Date(card.lastTransactionDate))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {dashboardQuery.isPending && selectedTags.length > 0 && (
          <p>Carregando...</p>
        )}
        {dashboardQuery.error && (
          <p className="text-red-500">Erro ao carregar dados das tags</p>
        )}

        {selectedTags.length > 0 &&
          tagCards.length === 0 &&
          !dashboardQuery.isPending && (
            <p className="text-muted-foreground">
              Nenhuma transação encontrada para as tags selecionadas neste
              período.
            </p>
          )}
      </div>
    </AuthGuard>
  );
}
